import React, { useState, useRef } from 'react';

import {
  Box,
  Card,
  CardContent,
  Typography,

  Chip,


  Button,
} from '@mui/material';
import ModalFinishCheckin from './ModalFinishCheckin';
import ModalCancelCheckin from './ModalCancelCheckin';
import {
  AccessTime,
  CheckCircle,
  Cancel,
  CheckCircleOutline,
  Error,

  Warning,

  HourglassEmpty,
} from '@mui/icons-material';
import { DriverCheckin } from '../types';
import { useShouldStackChips } from '../hooks/useIsOverflow';

interface CheckinCardProps {

  driver: DriverCheckin;
  actionLoading: number | null;
  onFinish: (id: number, observation: string | null) => void;

  onCancel: (id: number, reason: string) => void;
}




// Função para obter ícone e cor do status
const getStatusBadge = (status?: string) => {
  switch (status) {
    case 'Não há pedido emitido':
      return { icon: <Error />, color: 'error' as const, label: 'Não há pedido' };
    case 'Não liberado pelo financeiro':
      return { icon: <Warning />, color: 'warning' as const, label: 'Pendente Financeiro' };

    case 'Não liberado pelo comercial':
      return { icon: <Warning />, color: 'warning' as const, label: 'Pendente Comercial' };

    case 'Não liberado pelo transporte':
      return { icon: <Warning />, color: 'warning' as const, label: 'Pendente Transporte' };
    case 'Pedido liberado':
      return { icon: <CheckCircleOutline />, color: 'success' as const, label: 'Liberado' };
    default:
      return { icon: <HourglassEmpty />, color: 'default' as const, label: 'Desconhecido' };
  }
};

const censorCpf = (cpf: string): string => {
  if (!cpf || cpf.length < 3) return cpf;
  return '***.' + cpf.slice(-3);
};


function formatDuration(start: string | Date, end?: string | Date): string {
  const startTime = new Date(start).getTime();
  const endTime = end ? new Date(end).getTime() : Date.now();

  const diffMs = endTime - startTime;

  if (isNaN(diffMs) || diffMs < 0) return "-";

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  let result = "";
  if (days > 0) result += `${days}d `;
  if (hours > 0) result += `${hours}h `;
  if (minutes > 0 || result === "") result += `${minutes}min`;

  return result.trim();
}



const cancelReasons = [
  'Motorista desistiu',

  'Problema na documentação',
  'Carga não disponível',
  'Outros',
];

const CheckinCard: React.FC<CheckinCardProps> = ({
  driver,
  actionLoading,
  onFinish,
  onCancel,
}) => {
  const { icon, color, label } = getStatusBadge(driver.order_status);
  const [openFinishModal, setOpenFinishModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  // dentro do componente

  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const shouldStackChips = useShouldStackChips(chipsContainerRef);



  // Removendo estados de modal que foram movidos para os componentes modais
  // const [selectedCancelReason, setSelectedCancelReason] = useState('');
  // const [otherCancelReason, setOtherCancelReason] = useState('');

  const handleFinishClick = () => setOpenFinishModal(true);
  const handleConfirmFinish = (observation: string | null) => {
    onFinish(driver.id, observation);
    setOpenFinishModal(false);

  };


  const handleCancelClick = () => setOpenCancelModal(true);

  const handleConfirmCancel = (reason: string) => {
    onCancel(driver.id, reason);
    setOpenCancelModal(false);
    // Não limpa os estados aqui, pois eles foram movidos para o ModalCancelCheckin
  };


  const isFinishButtonDisabled = driver.order_status !== 'Pedido liberado';


  return (
    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Card
        sx={{
          mb: 1,
          width: '95%',

          borderLeft: `6px solid ${driver.status === 'Finalizado'
            ? '#4caf50'
            : driver.status === 'Cancelado'
              ? '#f44336'
              : color === 'success'
                ? '#4caf50'
                : color === 'warning'
                  ? '#ff9800'
                  : color === 'error'

                    ? '#f44336'
                    : '#9e9e9e'

            }`,

          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, position: 'relative' }}>

          {/* Layout Principal para Telas Grandes (SM+) */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            {/* Coluna 1: Nome e Informações */}
            <Box sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, width: { xs: '100%', sm: '55%', md: '60%' } }}>
              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                {driver.driver_name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Placa: <strong>{driver.license_plate}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  CPF: {censorCpf(driver.cpf)}
                </Typography>
                {driver.cliente_nome && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: { xs: '100%', sm: '100%' }, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    Razão: {driver.cliente_nome}
                  </Typography>
                )}
                {driver.numeroPedido && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Nº Pedido: {driver.numeroPedido}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Coluna 2: Chips de Status (na mesma linha) */}
            {driver.status === 'Em Fila' && (
              <Box
                ref={chipsContainerRef}
                sx={{
                  display: 'flex',
                  flexDirection: shouldStackChips ? 'column' : 'row',
                  alignItems: 'center',
                  gap: 1,
                  width: { xs: '100%', sm: '30%', md: '25%' },
                  transition: 'flex-direction 0.2s ease',
                  minWidth: 0,
                }}
              >
                <Chip
                  icon={<AccessTime />}
                  label={`Aguardando: ${formatDuration(driver.checkin_time)}`}
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    height: { xs: 26, sm: 22 },
                    fontSize: { xs: '0.65rem', sm: '0.55rem' },
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    flexShrink: 0,
                    maxWidth: '100%',
                  }}
                />
                <Chip
                  icon={icon}
                  label={label}
                  color={color}
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    height: { xs: 26, sm: 22 },
                    fontSize: { xs: '0.65rem', sm: '0.55rem' },
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    flexShrink: 0,
                    maxWidth: '100%',
                  }}
                />
              </Box>



            )}

            {/* Coluna 3: Botões (em coluna) */}
            <Box sx={{ display: 'flex', ml: 0.5, flexDirection: 'column', gap: 0.5, width: { xs: '100%', sm: '15%', md: '15%' }, alignItems: 'flex-end' }}>
              {/* Botão Finalizar */}
              {(driver.status) === 'Em Fila' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleFinishClick}
                  disabled={actionLoading === driver.id || isFinishButtonDisabled}
                  sx={{ height: { xs: 30, sm: 25 }, fontSize: { xs: '0.7rem', sm: '0.6rem' }, width: '100%' }}
                >
                  Finalizar
                </Button>
              )}

              {/* Botão Cancelar */}
              {driver.status === 'Em Fila' && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={handleCancelClick}
                  disabled={actionLoading === driver.id}
                  sx={{ height: { xs: 30, sm: 25 }, fontSize: { xs: '0.7rem', sm: '0.6rem' }, width: '100%' }}
                >
                  Cancelar
                </Button>
              )}

              {/* Chip de Status (Para status Finalizado/Cancelado) */}
              {(driver.status === 'Cancelado' || driver.status === 'Finalizado') && (
                <Chip
                  icon={
                    driver.status === 'Cancelado'
                      ? <Cancel />
                      : <CheckCircle />
                  }
                  label={
                    driver.status === 'Cancelado'
                      ? 'Cancelado'
                      : 'Finalizado'
                  }
                  color={
                    driver.status === 'Cancelado'
                      ? 'error'
                      : 'success'
                  }
                  size="small"
                  sx={{ fontWeight: 'bold', height: 24, fontSize: '0.65rem', width: '100%' }}
                />
              )}
            </Box>
          </Box>

          {/* Layout para Telas Pequenas (XS) - Manter o layout anterior que funcionou */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            {/* Chips de Status e Tempo (Apenas se estiver "Em Fila") */}
            {driver.status === 'Em Fila' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                  width: '100%',
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                {/* Chip de Aguardando */}
                <Chip
                  icon={<AccessTime />}
                  label={`Aguardando: ${formatDuration(driver.checkin_time)}`}
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold', height: 26, fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                />

                {/* Chip de Status */}
                <Chip
                  icon={icon}
                  label={label}
                  color={color}
                  size="small"
                  sx={{ fontWeight: 'bold', height: 24, fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                />
              </Box>
            )}

            {/* Linha principal: nome + status/botões */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                mb: 0.5,
                flexWrap: 'wrap',
              }}
            >
              {/* Nome do motorista */}
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                noWrap
                sx={{ flexGrow: 1, minWidth: 0 }}
              >
                {driver.driver_name}
              </Typography>

              {/* Botões/Status Finalizado/Cancelado */}
              {/* Status Finalizado/Cancelado (Mobile) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {/* Chip de Status (Para status Finalizado/Cancelado) */}
                {(driver.status === 'Cancelado' || driver.status === 'Finalizado') && (
                  <Chip
                    icon={
                      driver.status === 'Cancelado'
                        ? <Cancel />
                        : <CheckCircle />
                    }
                    label={
                      driver.status === 'Cancelado'
                        ? 'Cancelado'
                        : 'Finalizado'
                    }
                    color={
                      driver.status === 'Cancelado'
                        ? 'error'
                        : 'success'
                    }
                    size="small"
                    sx={{ fontWeight: 'bold', height: 24, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            </Box>

            {/* Linha inferior: placa, CPF, pedido + botões (mobile) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 0.5 }}>

              {/* Informações do motorista */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0, flex: 1, flexDirection: 'column' }}>

                <Typography variant="caption" color="text.secondary" noWrap>
                  Placa: <strong>{driver.license_plate}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  CPF: {censorCpf(driver.cpf)}
                </Typography>
                {driver.cliente_nome && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    Razão: {driver.cliente_nome}
                  </Typography>

                )}

                {driver.numeroPedido && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Nº Pedido: {driver.numeroPedido}
                  </Typography>

                )}
              </Box>

              {/* Botões Finalizar e Cancelar (Apenas se estiver "Em Fila") */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                {driver.status === 'Em Fila' && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={handleFinishClick}
                      disabled={actionLoading === driver.id || isFinishButtonDisabled}
                      sx={{ height: 30, fontSize: '0.7rem', flexGrow: 1 }}
                    >
                      Finalizar
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={handleCancelClick}
                      disabled={actionLoading === driver.id}
                      sx={{ height: 30, fontSize: '0.7rem', flexGrow: 1 }}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
              </Box>

            </Box>
          </Box>








          {/* Exibição da Observação */}
          {driver.status === 'Finalizado' && driver.observation && (
            <Box
              sx={{
                mt: 0.5,
                p: 0.5,
                borderLeft: '3px solid #4caf50',
                backgroundColor: '#f1f8e9',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                overflow: 'hidden',
              }}
            >
              <Typography

                variant="caption"
                fontWeight="bold"
                color="text.primary"
                sx={{ flexShrink: 0 }}
              >
                Observação:
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{
                  maxWidth: 200, // ajuste conforme o layout

                  textOverflow: 'ellipsis',

                  overflow: 'hidden',
                  display: 'inline-block',
                }}
                title={driver.observation} // tooltip ao passar o mouse
              >
                {driver.observation}

              </Typography>
            </Box>
          )}

          {/* Exibição do Motivo de Cancelamento */}
          {driver.status === 'Cancelado' && driver.cancel_reason && (
            <Box
              sx={{
                mt: 0.5,

                p: 0.5,
                borderLeft: '3px solid #f44336',
                backgroundColor: '#ffebee',
                display: 'flex',
                alignItems: 'center',

                gap: 0.5,
                overflow: 'hidden',
              }}
            >
              <Typography

                variant="caption"
                fontWeight="bold"
                color="text.primary"
                sx={{ flexShrink: 0 }}
              >

                Motivo do Cancelamento:
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{
                  maxWidth: 200,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: 'inline-block',
                }}
                title={driver.cancel_reason}
              >
                {driver.cancel_reason}
              </Typography>
            </Box>
          )}



          {(driver.status === 'Finalizado' || driver.status === 'Cancelado') && (

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
              <Typography variant="caption" color="text.secondary">

                Início: {new Date(driver.checkin_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>


              {driver.finish_time && (
                <Typography variant="caption" color="text.secondary">
                  Tempo: {formatDuration(driver.checkin_time, driver.finish_time)}
                </Typography>
              )}



              {driver.finish_time && (
                <Typography variant="caption" color="text.secondary">
                  Fim: {new Date(driver.finish_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              )}
            </Box>
          )}

        </CardContent>
      </Card>



      {/* Modal de Confirmação para Finalizar (Componente Modular) */}
      <ModalFinishCheckin
        open={openFinishModal}
        onClose={() => setOpenFinishModal(false)}
        onConfirm={handleConfirmFinish}

        driver={driver}
      />


      {/* Modal de Confirmação para Cancelar (Componente Modular) */}

      <ModalCancelCheckin
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        onConfirm={handleConfirmCancel}
        driver={driver}
        cancelReasons={cancelReasons}
      />

    </Box >
  );
};


export default CheckinCard;
